import { PlusIcon } from "@xola/icons";
import { Button } from "@xola/ui-kit";
import React from "react";
import { useHistory } from "react-router-dom";
import { Permission, Purchase, PurchaseStatus, Traveler } from "types";
import { useTraveler } from "../../../hooks/resources/useTraveler";
import { useAuthorization } from "../../../hooks/useAuthorization";
import { useCollection } from "../../../hooks/useCollection";
import { useCreatePurchaseStore } from "../../../stores/createPurchaseStore";
import { IsGranted } from "../../Common/IsGranted";
import { NoTranslate } from "../../Common/NoTranslate";
import { ContactCardList } from "./ContactCardList";
import { ContactData } from "./ContactData";
import { ContactInformation } from "./ContactInformation";
import { ContactPurchaseInformation } from "./ContactPurchaseInformation";
import { CustomerNotes } from "./CustomerNotes";
import { MergedCustomerRecords } from "./MergedCustomerRecords";
import { ContactTimeline } from "./Timeline/ContactTimeline";

export const ContactDetails = ({ contact }: { contact: Traveler }) => {
    const history = useHistory();
    const { isGranted } = useAuthorization();
    const resetState = useCreatePurchaseStore((store) => store.actions.resetState);

    const { traveler } = useTraveler(
        contact.id,
        { private: true },
        { enabled: isGranted(Permission.MODIFY_CUSTOMER_DETAILS) },
    );

    const {
        data: purchases,
        isLoading,
        isFetching,
    } = useCollection<Purchase>(
        "purchases",
        { travelers: contact.id, status: PurchaseStatus.COMMITTED },
        { staleTime: 3000, keepPreviousData: true },
    );

    const handlePurchaseButtonClick = () => {
        resetState();
        useCreatePurchaseStore.setState({ organizer: contact });
        history.push("/cart/init");
    };

    return (
        <div className="flex w-full flex-col">
            <div className="flex justify-between">
                <NoTranslate as="h2" data-testid="contact-details-name">
                    {contact.name}
                </NoTranslate>
                <IsGranted permissions={Permission.ADD_BOOKING_VIEW}>
                    <Button color="success" icon={<PlusIcon />} onClick={handlePurchaseButtonClick}>
                        Purchase
                    </Button>
                </IsGranted>
            </div>

            <div className="mb-4">
                <ContactData contact={contact} />
            </div>

            <div className="space-y-10 divide-y-2 divide-gray-lighter">
                <ContactPurchaseInformation purchases={purchases} contact={contact} />

                <ContactInformation contact={traveler ?? contact} purchases={purchases} />
                <IsGranted permissions={Permission.MODIFY_CUSTOMER_DETAILS}>
                    <MergedCustomerRecords contact={traveler ?? contact} />
                </IsGranted>
                <IsGranted permissions={Permission.MODIFY_CUSTOMER_DETAILS}>
                    <ContactCardList contact={contact} cards={traveler?.creditCards} />
                </IsGranted>

                <CustomerNotes contact={contact} />

                <ContactTimeline purchases={purchases} contact={contact} isLoading={isLoading || isFetching} />
            </div>
        </div>
    );
};
